import React, { memo, useEffect, useState } from 'react';
import { Button, Popconfirm } from 'antd';
import { UploadedCSV, QueryFilter } from './utils';
import {
  useStopCSVMail,
  useResumeCSVMail,
  usePauseCSVMail,
  useTriggerCSVMail,
} from 'mutation/mail';
import { differenceInMilliseconds, format } from 'date-fns';

const ButtonGroup = Button.Group;

type CSVActionProps = {
  // eslint-disable-next-line no-undef
  data: Pick<UploadedCSV, 'status' | 'totalRows' | 'id'>,
  queryFilter: QueryFilter,
  myId: number,
  canInterruptMailSending: boolean,
  onClickDetails: () => () => void,
  hideDetails: boolean,
};

function CSVAction(props: CSVActionProps) {
  const {
    onClickDetails,
    data,
    queryFilter,
    canInterruptMailSending,
    myId,
  } = props;

  const { mutate: stopCSVMail, isLoading: isStopping } = useStopCSVMail(
    queryFilter,
  );

  const { mutate: pauseCSVMail, isLoading: isPausing } = usePauseCSVMail(
    queryFilter,
  );

  const { mutate: resumeCSVMail, isLoading: isResuming } = useResumeCSVMail(
    queryFilter,
  );

  const { mutate: triggerCSVMail, isLoading: isTriggering } = useTriggerCSVMail(
    queryFilter,
  );

  const isScheduled = data.status === 'scheduled';
  const isPaused = data.isPaused;
  const isAborted = data.isAborted;
  const isCompleted = data.status === 'completed';
  const isTriggered =
    (data.status !== 'uploaded' && data.status !== 'scheduled') ||
    data.triggeredAt;
  const isDone = isAborted || isCompleted;
  const userId = data.user?.id || data.userId;
  const canInterrupt = canInterruptMailSending || myId === userId;
  const isGmail = data.esp === 'Gmail accounts';

  const detailsButton = props.hideDetails ? null : (
    <Button
      id={`csv-details-${data.id}`}
      style={{
        backgroundColor: isPaused ? 'yellow' : isAborted ? 'red' : undefined,
      }}
      type="button"
      onClick={onClickDetails(data.id)}
    >
      Details
    </Button>
  );

  if (data.feautreName)
    return <DetailsOnlyButton detailsButton={detailsButton} />;

  if (!isGmail) {
    const fiveMinutes = 5 * 60 * 1000;
    const scheduledTime = data.scheduledTime
      ? new Date(data.scheduledTime).getTime()
      : new Date(data.uploadedTime).getTime() + fiveMinutes;

    return (
      <NonGmailButtons
        canInterrupt={canInterrupt}
        data={data}
        detailsButton={detailsButton}
        isStopping={isStopping}
        isTriggered={isTriggered}
        isTriggering={isTriggering}
        scheduledTime={scheduledTime}
        stopCSVMail={stopCSVMail}
        triggerCSVMail={triggerCSVMail}
      />
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <ButtonGroup style={{ display: 'flex' }}>
        {canInterrupt &&
          !isDone &&
          (isPaused ? (
            <Button
              icon="caret-right"
              loading={isResuming}
              onClick={() => resumeCSVMail(data.id)}
            />
          ) : (
            <Button
              disabled={isScheduled}
              icon="pause"
              loading={isPausing}
              onClick={() => pauseCSVMail(data.id)}
            />
          ))}

        {detailsButton}

        {canInterrupt && !isDone && (
          <Popconfirm
            cancelText="No"
            okText="Yes"
            title="Are you sure you want to abort sending this CSV?"
            onConfirm={() => stopCSVMail(data.id)}
          >
            <Button icon="close" loading={isStopping} type="danger" />
          </Popconfirm>
        )}
      </ButtonGroup>
    </div>
  );
}

const NonGmailButtons = ({
  stopCSVMail,
  triggerCSVMail,
  data,
  isStopping,
  canInterrupt,
  isTriggered,
  isTriggering,
  detailsButton,
  scheduledTime,
}) => {
  const [remainingTime, setRemainingTime] = useState(scheduledTime);

  useEffect(() => {
    const timeLeft = differenceInMilliseconds(scheduledTime, new Date());
    if (timeLeft <= 0) setRemainingTime(0);
    else setRemainingTime(timeLeft);

    const timer = setInterval(() => {
      setRemainingTime(differenceInMilliseconds(scheduledTime, new Date()));
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [scheduledTime]);

  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <ButtonGroup style={{ display: 'flex' }}>
        {remainingTime > 0 && canInterrupt && !isTriggered && (
          <>
            <Popconfirm
              cancelText="No"
              okText="Yes"
              title={`Are you sure you want to send this csv ${
                data.scheduledTime
                  ? `scheduled ${formatScheduleTime(data.scheduledTime)}`
                  : ''
              } now?`}
              onConfirm={() => triggerCSVMail(data.id)}
            >
              <Button loading={isTriggering} type="primary">
                Trigger
              </Button>
            </Popconfirm>
            <Button disabled>{formatTime(remainingTime)}</Button>
            <Popconfirm
              cancelText="No"
              okText="Yes"
              title="Are you sure you want to abort sending this CSV?"
              onConfirm={() => stopCSVMail(data.id)}
            >
              <Button loading={isStopping} type="danger">
                Abort
              </Button>
            </Popconfirm>
          </>
        )}

        {(isTriggered || remainingTime <= 0) && detailsButton}
      </ButtonGroup>
    </div>
  );
};

const DetailsOnlyButton = ({ detailsButton }) => {
  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <ButtonGroup style={{ display: 'flex' }}>{detailsButton}</ButtonGroup>
    </div>
  );
};

const formatTime = time => {
  if (time <= 0) return '';

  const hours = Math.floor(time / 3600000);
  const minutes = Math.floor((time % 3600000) / 60000);
  const seconds = Math.floor((time % 60000) / 1000);

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  } else if (minutes > 0) {
    return `${formattedMinutes}:${formattedSeconds}`;
  } else {
    return `00:${formattedSeconds}`;
  }
};

const formatScheduleTime = scheduledTime => {
  if (!scheduledTime) return '';
  const date = new Date(scheduledTime);
  const timeFormat = format(date, 'HH:mm');
  const dateFormat = format(date, 'MMM dd, yyyy');
  return `on ${dateFormat} at ${timeFormat}`;
};

export default memo(CSVAction);
